<template>
  <template v-if="!loading">
    <WdsStack direction-column gap-md>
      <div><!--Helps apply the md gap --></div>
      <WdsDivider />

      <WdsStack direction-column gap-sm>
        <WdsStack justify-space-between align-center class="tw-flex-wrap lg:tw-flex-nowrap tw-px-2">
          <WdsText small class="compliance-actor-title-style" :text="$t('HIRE ACTIONS')" />
          <WdsText
            v-if="companyCompliances.length === 0 && !$slots['hire-actions']"
            :text="$t('No actions required. New items will display here.')"
            small
            secondary
          />
        </WdsStack>
        <WdsStack
          v-if="companyCompliances.length > 0 || $slots['hire-actions']"
          direction-column
          gap-xs
          class="tw-px-2"
        >
          <slot name="hire-actions"></slot>
          <HireComplianceAlert
            v-for="compliance in companyCompliances"
            :key="compliance.name"
            :compliance="compliance"
            :hire-id="hireId"
          />
        </WdsStack>
      </WdsStack>

      <WdsDivider />

      <WdsStack direction-column gap-sm>
        <WdsStack align-center justify-space-between class="tw-flex-wrap lg:tw-flex-nowrap tw-px-2">
          <WdsText
            small
            class="compliance-actor-title-style"
            :text="
              hasRecruiterManagingWorkers ? $t('STAFFING AGENCY ACTIONS') : $t('WORKER ACTIONS')
            "
          />
          <WdsText
            v-if="workerCompliances.length > 0"
            :text="
              hasRecruiterManagingWorkers
                ? $t('Track outstanding tasks for the staffing agency.')
                : $t('Track outstanding tasks for the worker.')
            "
            small
            secondary
          />
          <WdsText
            v-else
            :text="
              hasRecruiterManagingWorkers
                ? $t('No outstanding tasks for the staffing agency.')
                : $t('No outstanding tasks for the worker.')
            "
            small
            secondary
          />
        </WdsStack>
        <WdsStack
          v-if="workerCompliances.length > 0 || $slots['worker-actions']"
          direction-column
          gap-xs
          class="tw-px-2"
        >
          <slot name="worker-actions"></slot>
          <WdsStack
            v-for="compliance in workerCompliances"
            :key="compliance.name"
            class="worker-actions"
            align-center
            justify-space-between
            :data-test="`compliance-status-${compliance.name}`"
          >
            <WdsText :text="compliance.title" />
          </WdsStack>
        </WdsStack>
      </WdsStack>
    </WdsStack>
  </template>
</template>

<script lang="ts" setup>
import { computed } from 'vue'
import { WdsDivider, WdsStack, WdsText } from '@worksome/wds-components'
import { useHireRecruiter } from '../composables/useHireRecruiter'
import HireComplianceAlert from '@/components/Hire/HirePageCompliance/HireComplianceAlert.vue'
import { ComplianceActorTypes } from '@/gql/graphql'
import { $t } from '@/app-bootstrapping/SetUpInternationalization'

const props = defineProps<{
  compliances: any[]
  loading: boolean
  hireId: string
}>()

const { hasRecruiterManagingWorkers } = useHireRecruiter(props.hireId)

// Separate computed properties for each actor type
const companyCompliances = computed(() =>
  props.compliances.filter(
    (c) =>
      (c.actor === ComplianceActorTypes.Company || c.actor === ComplianceActorTypes.Worksome) &&
      !c.completed,
  ),
)

const workerCompliances = computed(() =>
  props.compliances.filter(
    (c) =>
      (c.actor === ComplianceActorTypes.Worker || c.actor === ComplianceActorTypes.Recruiter) &&
      !c.completed,
  ),
)
</script>

<style scoped>

.compliance-actor-title-style:deep(div) {
  @apply tw-text-gray-800;
  white-space: nowrap;
  font-weight: 600;
}

.worker-actions {
  background-color: white;
  @apply tw-rounded tw-px-3 tw-py-2;
}
</style>
